/**
 * Phase 41 Plan 41-16 Task 1 — MyExpensesCard.
 *
 * Bottom-row left card on the v7 Personal Dashboard (per CONTEXT amendment
 * D-Dashboard-layout-v3, 2026-05-12). Shows the current user's three most
 * recent expense receipts plus an "Upload" affordance to start a new sheet.
 *
 * Data source: `useUserExpenseSheets()` from `hooks/useExpenses.ts` — the
 * same hook the legacy expenses page consumes. Sheets are sorted by
 * `createdAt` desc and the top three flattened receipts are rendered.
 *
 * T-41-V3-03 mitigation — receipt descriptions are user-generated and rendered
 * via React's default JSX escaping (no `dangerouslySetInnerHTML`).
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Btn } from '../ui/Btn';
import { Pill } from '../ui/Pill';
import { useUserExpenseSheets } from '../../hooks/useExpenses';
import { formatDisplayDate } from '../../utils/format';
import type { ExpenseSheet, ExpenseReceipt } from '../../services/api/expenses';
import { LEGACY_EN_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';

interface FlatRow {
  id: string;
  sheetId: number;
  label: string;
  state: string;
  amount: number;
  receiptDate: string | null;
}

function fmtCzk(n: number): string {
  return numberFormatter({
    style: 'currency',
    currency: 'CZK',
    maximumFractionDigits: 0,
  }, LEGACY_EN_NUMBER_LOCALE).format(n);
}

function statePill(state: string) {
  const norm = state.toLowerCase();
  if (norm === 'approved' || norm === 'reimbursed') {
    return (
      <Pill bg="#DCFCE7" fg="#166534">
        {norm === 'reimbursed' ? 'Reimbursed' : 'Approved'}
      </Pill>
    );
  }
  if (norm === 'submitted' || norm === 'pending') {
    return (
      <Pill bg="#FEF3C7" fg="#92400E">
        Submitted
      </Pill>
    );
  }
  if (norm === 'rejected') {
    return (
      <Pill bg="#FFE4E6" fg="#BE123C">
        Rejected
      </Pill>
    );
  }
  return (
    <Pill bg="#E0F2FE" fg="#075985">
      Open
    </Pill>
  );
}

function flatten(sheets: ExpenseSheet[]): FlatRow[] {
  const rows: FlatRow[] = [];
  for (const sheet of sheets) {
    if (!sheet.receipts || sheet.receipts.length === 0) {
      // Surface even empty sheets so the user sees the latest activity.
      rows.push({
        id: `s-${sheet.id}`,
        sheetId: sheet.id,
        label: sheet.description ?? `Sheet #${sheet.id}`,
        state: sheet.state,
        amount: sheet.totalAmount ?? 0,
        receiptDate: sheet.createdAt,
      });
      continue;
    }
    for (const r of sheet.receipts) {
      rows.push({
        id: `r-${sheet.id}-${r.id}`,
        sheetId: sheet.id,
        label: receiptLabel(r, sheet),
        state: sheet.state,
        amount: r.amount ?? 0,
        receiptDate: r.receiptDate ?? r.createdAt,
      });
    }
  }
  rows.sort((a, b) => {
    const ta = a.receiptDate ? Date.parse(a.receiptDate) : 0;
    const tb = b.receiptDate ? Date.parse(b.receiptDate) : 0;
    return tb - ta;
  });
  return rows;
}

function receiptLabel(r: ExpenseReceipt, sheet: ExpenseSheet): string {
  if (r.description && r.description.trim().length > 0) return r.description;
  if (sheet.description && sheet.description.trim().length > 0) return sheet.description;
  return `Receipt #${r.id}`;
}

export function MyExpensesCard() {
  const { data: sheets, isLoading } = useUserExpenseSheets();

  const { rows, totalAmount, pendingCount } = useMemo(() => {
    const list = sheets ?? [];
    const flat = flatten(list);
    const total = list.reduce((sum, s) => sum + (s.totalAmount ?? 0), 0);
    const pending = list.filter(
      (s) => s.state.toLowerCase() === 'submitted' || s.state.toLowerCase() === 'pending',
    ).length;
    return { rows: flat.slice(0, 3), totalAmount: total, pendingCount: pending };
  }, [sheets]);

  const subtitle =
    isLoading || !sheets
      ? 'Loading…'
      : `${fmtCzk(totalAmount)} total · ${pendingCount} pending`;

  return (
    <Card
      title="Recent expenses"
      action={<span className="text-[11px] text-ms-text-muted">{subtitle}</span>}
    >
      {isLoading ? (
        <div className="text-[12px] text-ms-text-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <p className="text-[12px] text-ms-text-muted mb-4">No expenses yet.</p>
      ) : (
        <ul className="divide-y divide-ms-border mb-4">
          {rows.map((row) => (
            <li key={row.id} className="py-2 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1 flex items-center gap-2">
                <span
                  aria-hidden
                  className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-ms-orange/10 text-ms-orange text-[11px] font-semibold shrink-0"
                >
                  $
                </span>
                <div className="min-w-0">
                  <div className="text-[12.5px] font-medium text-ms-text-strong truncate">
                    {row.label}
                  </div>
                  <div className="text-[10.5px] text-ms-text-muted">
                    {row.receiptDate
                      ? formatDisplayDate(row.receiptDate)
                      : '—'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {statePill(row.state)}
                <span className="text-[12px] font-semibold tabular-nums text-ms-text-strong">
                  {fmtCzk(row.amount)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="flex">
        <Link to="/expenses">
          <Btn variant="outline-accent" size="sm">
            + Upload
          </Btn>
        </Link>
      </div>
    </Card>
  );
}
